import { ProviderInfoFragment } from '@app/appointment/__generated__/patient-appointment-inventory-search-graphql.fragments.types';
import { PatientTaskProvider } from '@app/care-plan/patient-task/__generated__/patient-tasks-graphql.fragments.types';
import { Profile, UserType } from '@omgui/omgui-profile-bubble/omgui-profile-bubble.component';

export class Provider implements Profile {
  id: number;
  name: string;
  firstName: string;
  preferredName: string;
  lastName: string;
  profileImageUrl: string | null;
  type = UserType.InternalUser;
  suffix: string;
  outOfOffice: boolean;
  returnDate: string;
  email: string;
  displayName: string;
  colleagues: Provider[];
  serviceAreaId: number;
  preparedName: string;
  acceptingNewPatients: boolean;
  isCrossCredentialed: boolean;

  static preparedName(provider: Provider) {
    return provider.firstName + ' ' + provider.lastName + (provider.suffix ? ', ' + provider.suffix : '');
  }

  static fromGraphQL(object: PatientTaskProvider): Provider {
    const provider = new Provider();
    provider.id = +object.id;
    provider.displayName = object.displayName;
    provider.profileImageUrl = object.profileImageUrl;
    return provider;
  }

  static fromAppointmentSearchGraphQL(graphqlProvider: ProviderInfoFragment): Provider {
    const provider = new Provider();
    provider.id = parseInt(graphqlProvider.id, 10);
    provider.displayName = graphqlProvider.displayName;
    provider.profileImageUrl = graphqlProvider.image;
    provider.isCrossCredentialed = graphqlProvider.isCrossCredentialed;

    return provider;
  }

  static fromApiV2(providerObject: Record<string, any>): Provider {
    const provider = new Provider();
    provider.id = providerObject.id;
    provider.name = providerObject.name;
    provider.firstName = providerObject.first_name;
    provider.preferredName = providerObject.first_name;
    provider.lastName = providerObject.last_name;
    provider.type = providerObject.type;
    provider.suffix = providerObject.suffix;
    provider.outOfOffice = providerObject.out_of_office;
    provider.returnDate = providerObject.return_date;
    provider.email = providerObject.email;
    provider.displayName = providerObject.display_name;
    provider.serviceAreaId = providerObject.service_area_id;
    provider.acceptingNewPatients = providerObject.accepting_new_patients;
    provider.isCrossCredentialed = providerObject.is_cross_credentialed;
    provider.profileImageUrl = providerObject.profile_image_url;

    if (!provider.profileImageUrl && providerObject.image) {
      provider.profileImageUrl = providerObject.image;
    }

    provider.preparedName = Provider.preparedName(provider);

    if (providerObject.colleagues) {
      provider.colleagues = providerObject.colleagues.map((colleague: Record<string, any>) =>
        Provider.fromApiV2(colleague),
      );
    }

    return provider;
  }

  toApiV2() {
    const apiV2: Record<string, any> = {
      id: this.id,
      name: this.name,
      first_name: this.firstName,
      last_name: this.lastName,
      profile_image_url: this.profileImageUrl,
      type: this.type,
      suffix: this.suffix,
      out_of_office: this.outOfOffice,
      return_date: this.returnDate,
      email: this.email,
      display_name: this.displayName,
      service_area_id: this.serviceAreaId,
    };

    if (this.colleagues) {
      apiV2.colleagues = this.colleagues.map(colleague => colleague.toApiV2());
    }

    return apiV2;
  }
}

export function sortByLastName(p1: { lastName: string }, p2: { lastName: string }) {
  if (p1.lastName.toLowerCase() < p2.lastName.toLowerCase()) {
    return -1;
  }
  if (p1.lastName.toLowerCase() > p2.lastName.toLowerCase()) {
    return 1;
  }
  return 0;
}
